/**
 * AggregateSearchHelper - Config-driven helper for building aggregate search requests.
 *
 * EXTENSIBILITY: To make a new filter dynamic, simply add its field name to AGGREGATE_FIELDS.
 * No other file changes are required (saga, reducer, actions are all generic).
 */

/**
 * Central registry of fields to aggregate.
 * Order matters — response data[] follows this order.
 * To add a new dynamic filter, just append the field name here.
 */
export const AGGREGATE_FIELDS = [
    'subType',
    'landUse',
    'developmentZoneDescription',
];

export const AGGREGATE_SIZE = 10000;

/**
 * Builds the aggregations array for the aggregate search request.
 * @returns {Array<{field: string, method: string, size: number}>}
 */
export const buildAggregations = () =>
    AGGREGATE_FIELDS.map(field => ({ field, method: 'term', size: AGGREGATE_SIZE }));

/**
 * Builds the full request body for the aggregate search API.
 * @param {Array} filters - Array of filter objects in { field, operation, value } format
 * @returns {Object} - The request body for POST /api/batch/aggregate-search
 */
export const buildAggregateRequestBody = (filters = []) => ({
    requests: [
        {
            filters,
            aggregations: buildAggregations(),
        },
    ],
});

/**
 * Maps the aggregate API response data[] to an options object keyed by field name.
 * Options are sorted alphabetically for consistent display.
 * @param {Array} data - The response data array from the aggregate API
 * @returns {Object} - { [fieldName]: string[] } mapping
 *
 * Example output:
 * {
 *   subType: ['House', 'Unit', ...],
 *   landUse: ['Residential', ...],
 *   developmentZoneDescription: ['General Residential', ...]
 * }
 */
export const mapResponseToOptions = (data = []) => {
    const options = {};
    AGGREGATE_FIELDS.forEach((fieldName, index) => {
        if (data[index] && data[index].fieldValues) {
            options[fieldName] = data[index].fieldValues
                .map(fv => fv.value)
                .sort((a, b) => a.localeCompare(b));
        }
    });
    return options;
};

/**
 * Fields that supplement baseFilters for aggregate API calls.
 * These are locality-related modifiers not already included in baseFilters.
 */
const NON_BASE_FILTER_FIELDS = [
    'radius',
    'mapSearch',
    'isNonAddressSearch',
];

/**
 * Extracts only the non-base filter fields from a flat filters object.
 * These supplement props.baseFilters (which already contains the core locality
 * context like suggestions, lat, lon, etc.) with additional modifiers needed
 * by the aggregate API.
 *
 * @param {Object} flatFilters - The full flat filters object
 * @returns {Object} - Only the non-base fields that have defined values
 */
export const extractNonBaseFilters = (flatFilters) => {
    if (!flatFilters) return {};
    const result = {};
    NON_BASE_FILTER_FIELDS.forEach(field => {
        if (flatFilters[field] !== undefined && flatFilters[field] !== null) {
            result[field] = flatFilters[field];
        }
    });
    return result;
};

export default {
    AGGREGATE_FIELDS,
    AGGREGATE_SIZE,
    buildAggregations,
    buildAggregateRequestBody,
    mapResponseToOptions,
};
